import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Command, User, Monitor, ExternalLink, Sun, Moon, Settings, Scale, LogOut, Sparkles, Plus, MessageSquare, LayoutGrid, X, Pin, Bell, AlertCircle, AlertTriangle, Info, CheckCircle2, Menu, PanelLeftOpen, Check, CheckCheck, Shield, Filter, Trash2 } from 'lucide-react';
import { Input } from '../ui/Input';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useTheme } from '../../hooks/useTheme';
import { useNotifications, getIcon, getBadgeStyle, generateMeaningLine, getBadgeLabel, getIconStyle, getUICategory, AppNotification } from '../../hooks/useNotifications';
import { cn } from '../ui/Button';
interface TopBarProps {
  onToggleAi?: () => void;
  onOpenAi?: () => void;
  isAiOpen?: boolean;
  dockedWidth?: number;
  onCreateWorkspace?: () => void;
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
}
export function TopBar({
  onToggleAi,
  onOpenAi,
  isAiOpen,
  dockedWidth = 380,
  onCreateWorkspace,
  isSidebarOpen,
  onToggleSidebar
}: TopBarProps) {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const {
    chats,
    setActiveChatId
  } = useChatHistory();
  const {
    workspaces,
    setActiveWorkspace
  } = useWorkspaces();
  const {
    notifications,
    unreadCount,
    markAllRead,
    markAsRead,
    dismissNotification,
    toggleRead
  } = useNotifications();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [notifFilter, setNotifFilter] = useState<string>('All');
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const aiButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const alertsButtonRef = useRef<HTMLButtonElement>(null);
  const alertsMenuRef = useRef<HTMLDivElement>(null);
  const [gradientPos, setGradientPos] = useState({
    x: 50,
    y: 50
  });
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const [searchDropdownPos, setSearchDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  // Filter results
  const query = searchQuery.toLowerCase().trim();
  const matchedChats = query ? chats.filter(c => c.title.toLowerCase().includes(query) || c.messages.some(m => m.content.toLowerCase().includes(query))).slice(0, 5) : [];
  const matchedWorkspaces = query ? workspaces.filter(w => w.name.toLowerCase().includes(query) || w.description.toLowerCase().includes(query)).slice(0, 5) : [];
  const hasResults = matchedChats.length > 0 || matchedWorkspaces.length > 0;
  const showDropdown = searchFocused && query.length > 0;
  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    if (notifFilter === 'All') return true;
    if (notifFilter === 'Unread') return !n.read;
    return getUICategory(n.notification_type) === notifFilter;
  });
  const sortedNotifications = [...filteredNotifications].sort((a, b) => new Date(b.sort_date).getTime() - new Date(a.sort_date).getTime());
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };
  // Update dropdown position when search is focused
  useEffect(() => {
    if (showDropdown && searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setSearchDropdownPos({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
  }, [showDropdown, searchQuery]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (buttonRef.current && !buttonRef.current.contains(target) && menuRef.current && !menuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(target) && searchDropdownRef.current && !searchDropdownRef.current.contains(target)) {
        setSearchFocused(false);
      }
      if (alertsButtonRef.current && !alertsButtonRef.current.contains(target) && alertsMenuRef.current && !alertsMenuRef.current.contains(target)) {
        setAlertsOpen(false);
        setNotifFilter('All'); // Reset filter on close
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Cmd+K shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (window.innerWidth < 768) {
          setMobileSearchOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 50);
        } else {
          searchInputRef.current?.focus();
        }
      }
      if (e.key === 'Escape') {
        setSearchFocused(false);
        setSearchQuery('');
        setMobileSearchOpen(false);
        setAlertsOpen(false);
        setNotifFilter('All');
        searchInputRef.current?.blur();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  // Track mouse across entire viewport
  useEffect(() => {
    let rafId: number;
    function handleGlobalMouseMove(e: MouseEvent) {
      rafId = requestAnimationFrame(() => {
        const x = e.clientX / window.innerWidth * 100;
        const y = e.clientY / window.innerHeight * 100;
        setGradientPos({
          x,
          y
        });
      });
    }
    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      cancelAnimationFrame(rafId);
    };
  }, []);
  const handleSelectChat = (chatId: string) => {
    setActiveChatId(chatId);
    setSearchQuery('');
    setSearchFocused(false);
    setMobileSearchOpen(false);
    onOpenAi?.();
  };
  const handleSelectWorkspace = (wsId: string) => {
    setActiveWorkspace(wsId);
    setSearchQuery('');
    setSearchFocused(false);
    setMobileSearchOpen(false);
    navigate(`/workspace/${wsId}`);
  };
  const searchDropdown = showDropdown ? ReactDOM.createPortal(<div ref={searchDropdownRef} className="fixed rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden max-h-[400px] overflow-y-auto" style={{
    top: searchDropdownPos.top,
    left: searchDropdownPos.left,
    width: searchDropdownPos.width,
    zIndex: 9999,
    background: 'rgba(15, 23, 42, 0.96)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  }} data-id="element-915">
          <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
    }} data-id="element-916" />
          {!hasResults && <div className="relative px-4 py-6 text-center text-xs text-white/30" data-id="element-917">
              No results for &ldquo;{searchQuery}&rdquo;
            </div>}
          {matchedWorkspaces.length > 0 && <div className="relative" data-id="element-918">
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-white/30 uppercase tracking-wider flex items-center gap-1.5" data-id="element-919">
                <LayoutGrid size={10} data-id="element-920" /> Workspaces
              </div>
              {matchedWorkspaces.map(ws => <button key={ws.id} onClick={() => handleSelectWorkspace(ws.id)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.06] transition-colors" data-id="element-921">
                  <div className="h-7 w-7 rounded-md bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[10px] font-bold text-white/50 flex-shrink-0" data-id="element-922">
                    {ws.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-923">
                    <div className="text-xs font-medium text-white/90 truncate flex items-center gap-1.5" data-id="element-924">
                      {ws.name}
                      {ws.pinned && <Pin size={8} className="text-[#EC4899]/60 flex-shrink-0" data-id="element-925" />}
                    </div>
                    <div className="text-[10px] text-white/35 truncate" data-id="element-926">
                      {ws.description}
                    </div>
                  </div>
                </button>)}
            </div>}
          {matchedChats.length > 0 && <div className="relative" data-id="element-927">
              {matchedWorkspaces.length > 0 && <div className="mx-3 border-t border-white/[0.06]" data-id="element-928" />}
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-white/30 uppercase tracking-wider flex items-center gap-1.5" data-id="element-929">
                <MessageSquare size={10} data-id="element-930" /> Chats
              </div>
              {matchedChats.map(chat => <button key={chat.id} onClick={() => handleSelectChat(chat.id)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.06] transition-colors" data-id="element-931">
                  <div className="h-7 w-7 rounded-md bg-[#EC4899]/10 border border-[#EC4899]/15 flex items-center justify-center flex-shrink-0" data-id="element-932">
                    <MessageSquare size={12} className="text-[#EC4899]/70" data-id="element-933" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-934">
                    <div className="text-xs font-medium text-white/90 truncate flex items-center gap-1.5" data-id="element-935">
                      {chat.title}
                      {chat.pinned && <Pin size={8} className="text-[#EC4899]/60 flex-shrink-0" data-id="element-936" />}
                    </div>
                    <div className="text-[10px] text-white/35 truncate" data-id="element-937">
                      {chat.messages.length} message
                      {chat.messages.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </button>)}
            </div>}
        </div>, document.body) : null;
  return <header className="app-topbar fixed top-0 z-[55] h-16 flex items-center gap-3 px-4 md:px-6 bg-[#0F172A]/90 backdrop-blur-sm border-b border-slate-800/50 left-0" style={{
    left: isSidebarOpen ? '240px' : '0',
    right: isAiOpen ? `${dockedWidth}px` : '0',
    transition: 'left 300ms ease-in-out'
  }} data-id="element-938">
      {/* Hamburger — mobile */}
      <button onClick={onToggleSidebar} className="lg:hidden p-2 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.06] transition-colors flex-shrink-0" data-id="element-939">
        <Menu size={20} data-id="element-940" />
      </button>

      {/* Sidebar reopen button — desktop, only when sidebar is closed */}
      {!isSidebarOpen && <button onClick={onToggleSidebar} className="hidden lg:flex p-2 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.06] transition-colors flex-shrink-0" title="Open sidebar" data-id="element-941">
          <PanelLeftOpen size={20} data-id="element-942" />
        </button>}

      {/* Search — desktop always visible, hidden below lg */}
      <div ref={searchRef} className="flex-1 min-w-0 relative hidden md:block" data-id="element-943">
        <div ref={searchContainerRef} className="relative max-w-xl" data-id="element-944">
          <input ref={searchInputRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} placeholder="Search chats, workspaces..." className="w-full h-9 pl-9 pr-16 rounded-lg bg-[#1E293B] border border-slate-700/50 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#EC4899]/40 focus:ring-1 focus:ring-[#EC4899]/10 transition-all" data-id="element-945" />
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-946" />
          {searchQuery ? <button onClick={() => {
          setSearchQuery('');
          searchInputRef.current?.focus();
        }} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-white/30 hover:text-white/60 transition-colors" data-id="element-947">
              <X size={14} data-id="element-948" />
            </button> : <div className="search-shortcut absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-600 bg-slate-800 text-[10px] text-slate-400 font-medium" data-id="element-949">
              <Command size={10} data-id="element-950" />
              <span data-id="element-951">K</span>
            </div>}
          {searchDropdown}
        </div>
      </div>

      {/* Mobile/tablet search icon — visible below lg */}
      <button onClick={() => {
      setMobileSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }} className="md:hidden p-2 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.06] transition-colors flex-shrink-0" data-id="element-952">
        <Search size={18} data-id="element-953" />
      </button>

      {/* Mobile search overlay */}
      {mobileSearchOpen && <div className="fixed inset-0 z-[200] bg-[#0B1120]/95 backdrop-blur-sm md:hidden" data-id="element-954">
          <div className="p-4" data-id="element-955">
            <div ref={searchRef} className="relative" data-id="element-956">
              <input ref={searchInputRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} placeholder="Search chats, workspaces..." autoFocus className="w-full h-12 pl-10 pr-12 rounded-xl bg-[#1E293B] border border-slate-700/50 text-base text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#EC4899]/40 focus:ring-1 focus:ring-[#EC4899]/10 transition-all" data-id="element-957" />
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-958" />
              <button onClick={() => {
            setMobileSearchOpen(false);
            setSearchQuery('');
            setSearchFocused(false);
          }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/80 transition-colors" data-id="element-959">
                <X size={18} data-id="element-960" />
              </button>
              {searchDropdown}
            </div>
          </div>
        </div>}

      {/* Right actions */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0" data-id="element-961">
        {/* Notifications Bell */}
        <div className="relative" data-id="element-962">
          <button ref={alertsButtonRef} onClick={() => setAlertsOpen(!alertsOpen)} className="relative p-2 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.06] transition-colors flex-shrink-0" title="Notifications" data-id="element-963">
            <Bell size={17} data-id="element-964" />
            {unreadCount > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center shadow-[0_0_8px_rgba(239,68,68,0.6)]" data-id="element-965">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>}
          </button>

          {alertsOpen && ReactDOM.createPortal(<div ref={alertsMenuRef} data-portal-menu className="jua-notif-panel fixed w-[480px] max-w-[calc(100vw-32px)] rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
          top: alertsButtonRef.current ? alertsButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
          right: 16,
          zIndex: 9999,
          maxHeight: '600px',
          background: 'rgba(15, 23, 42, 0.96)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)'
        }} data-id="element-966">
                <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
          }} data-id="element-967" />

                {/* Header */}
                <div className="relative z-10 flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0" data-id="element-968">
                  <div className="flex items-center gap-2" data-id="element-969">
                    <Bell size={14} className="text-white/50" data-id="element-970" />
                    <span className="text-sm font-semibold text-white" data-id="element-971">
                      Notifications
                    </span>
                    {unreadCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium border border-red-500/20" data-id="element-972">
                        {unreadCount} new
                      </span>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-973">
                    <button onClick={() => markAllRead()} className="text-[11px] text-white/40 hover:text-white/80 transition-colors flex items-center gap-1.5" disabled={unreadCount === 0} data-id="element-974">
                      <CheckCheck size={12} data-id="element-975" />
                      Mark all read
                    </button>
                    <button onClick={() => {
                setAlertsOpen(false);
                navigate('/alerts');
              }} className="text-[11px] text-[#EC4899] hover:text-[#db2777] font-medium transition-colors" data-id="element-976">
                      Manage Alerts
                    </button>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="relative z-10 border-b border-white/[0.06] overflow-x-auto flex-shrink-0" data-id="element-977">
                  <div className="flex items-center gap-1 px-3" data-id="element-978">
                    {([{
                key: 'All',
                label: 'All'
              }, {
                key: 'Unread',
                label: 'Unread'
              }, {
                key: 'Forecast update',
                label: 'Forecast'
              }, {
                key: 'Market signal',
                label: 'Market'
              }, {
                key: 'Model revision',
                label: 'Revision'
              }, {
                key: 'System',
                label: 'System'
              }] as const).map(f => <button key={f.key} onClick={() => setNotifFilter(f.key)} className={cn('py-2.5 px-2.5 text-[11px] font-medium transition-all relative whitespace-nowrap', notifFilter === f.key ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#EC4899] after:rounded-full' : 'text-slate-500 hover:text-slate-300')} data-id="element-979">
                        {f.label}
                        {f.key === 'Unread' && unreadCount > 0 && <span className="ml-1 text-[9px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded-full" data-id="element-980">
                            {unreadCount}
                          </span>}
                      </button>)}
                  </div>
                </div>

                {/* Notification list */}
                <div className="relative overflow-y-auto flex-1 min-h-[300px]" data-id="element-981">
                  {sortedNotifications.length === 0 ? <div className="h-full flex flex-col items-center justify-center gap-3 text-white/25 p-8 text-center" data-id="element-982">
                      <div className="w-12 h-12 rounded-full bg-white/[0.03] flex items-center justify-center" data-id="element-983">
                        <Bell size={20} data-id="element-984" />
                      </div>
                      <div data-id="element-985">
                        <p className="text-sm font-medium text-white/40" data-id="element-986">
                          No notifications found
                        </p>
                        <p className="text-xs mt-1" data-id="element-987">
                          {notifFilter === 'Unread' ? "You're all caught up!" : 'Try adjusting your filters.'}
                        </p>
                      </div>
                      {notifFilter !== 'All' && <button onClick={() => setNotifFilter('All')} className="mt-2 text-xs text-[#EC4899] hover:text-[#db2777]" data-id="element-988">
                          Clear filters
                        </button>}
                    </div> : sortedNotifications.map(notification => {
              const NotifIcon = getIcon(notification.notification_type, notification.details, notification.severity);
              const badgeLabel = getBadgeLabel(notification.event_type, notification.notification_type);
              const badgeStyle = getBadgeStyle(notification.event_type);
              const iconStyle = getIconStyle(notification.notification_type, notification.severity, isLight);
              const meaningLine = generateMeaningLine(notification);
              return <div key={notification.id} className={cn('relative flex gap-3 px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors group', !notification.read && 'bg-white/[0.02]')} data-id="element-989">
                          {!notification.read && <div className="jua-unread-bar absolute left-0 top-1 bottom-1 w-[2.5px] rounded-full" data-id="element-990" />}

                          {/* Icon */}
                          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', iconStyle.bg, iconStyle.color, iconStyle.border)} data-id="element-991">
                            <NotifIcon size={14} data-id="element-992" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0" data-id="element-993">
                            <div className="flex items-start justify-between gap-2 mb-0.5" data-id="element-994">
                              <div className="flex flex-wrap items-center gap-1.5" data-id="element-995">
                                <span className={cn('text-xs leading-tight', !notification.read ? 'font-bold text-white' : 'font-medium text-slate-400')} data-id="element-996">
                                  {notification.alert_name}
                                </span>
                                <span className={cn('text-[9px] px-1 py-px rounded border font-medium uppercase tracking-wider', badgeStyle.bg, badgeStyle.text, badgeStyle.border)} data-id="element-997">
                                  {badgeLabel}
                                </span>
                              </div>
                              <span className="text-[10px] text-white/30 font-mono flex-shrink-0 whitespace-nowrap" data-id="element-998">
                                {formatTime(notification.sort_date)}
                              </span>
                            </div>

                            <p className={cn('text-[11px] leading-relaxed line-clamp-2 mb-1.5', !notification.read ? 'text-slate-300' : 'text-slate-500')} data-id="element-999">
                              {meaningLine}
                            </p>

                            <div className="flex items-center justify-between" data-id="element-1000">
                              <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-1001">
                                {notification.workspace_name && <span className="flex items-center gap-1" data-id="element-1002">
                                    <div className="w-1 h-1 rounded-full bg-slate-600" data-id="element-1003" />
                                    {notification.workspace_name}
                                  </span>}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1004">
                                <button onClick={e => {
                        e.stopPropagation();
                        toggleRead(notification.id);
                      }} className="px-1.5 py-0.5 rounded hover:bg-white/10 text-[10px] text-slate-400 hover:text-white transition-colors whitespace-nowrap" data-id="element-1005">
                                  {notification.read ? 'Mark as unread' : 'Mark as read'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>;
            })}
                </div>

                {/* Footer */}
                <div className="relative z-10 px-4 py-3 border-t border-white/[0.06] bg-black/20 flex-shrink-0" data-id="element-1006">
                  <button onClick={() => {
              setAlertsOpen(false);
              navigate('/alerts/create');
            }} className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-[#EC4899]/10 hover:bg-[#EC4899]/20 text-[#EC4899] text-xs font-medium transition-colors border border-[#EC4899]/20" data-id="element-1007">
                    <Plus size={14} data-id="element-1008" />
                    Create New Alert
                  </button>
                </div>
              </div>, document.body)}
        </div>

        {/* New Workspace Button */}
        {onCreateWorkspace && <button onClick={onCreateWorkspace} className="jua-topbar-new-ws h-9 px-3 md:px-3.5 rounded-xl flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white bg-white/[0.10] hover:bg-white/[0.16] border border-white/[0.12] hover:border-white/[0.22] transition-colors flex-shrink-0" title="Create new workspace" data-id="element-1009">
            <Plus size={14} data-id="element-1010" />
            <span className="hidden md:inline" data-id="element-1011">New Workspace</span>
          </button>}

        {/* Athena AI Toggle */}
        <button ref={aiButtonRef} onClick={onToggleAi} className="athena-btn group relative h-9 px-3 md:px-5 rounded-xl flex items-center gap-2 text-sm font-semibold overflow-hidden flex-shrink-0" title="Toggle Athena AI" style={{
        transition: 'box-shadow 0.3s ease',
        boxShadow: isAiOpen ? '0 0 28px rgba(168, 85, 247, 0.45), 0 0 10px rgba(236, 72, 153, 0.35)' : '0 0 18px rgba(168, 85, 247, 0.25), 0 0 6px rgba(236, 72, 153, 0.2)'
      }} data-id="element-1012">
          <div className="absolute -inset-4 pointer-events-none" style={{
          background: `
                radial-gradient(ellipse 120% 120% at ${gradientPos.x}% ${gradientPos.y}%, rgba(236,72,153,0.9) 0%, rgba(217,70,239,0.7) 20%, rgba(168,85,247,0.6) 35%, rgba(99,102,241,0.5) 50%, rgba(56,189,248,0.5) 65%, rgba(45,212,191,0.4) 80%, rgba(168,85,247,0.5) 100%)
              `,
          filter: 'blur(8px)'
        }} data-id="element-1013" />
          <div className="absolute inset-0 rounded-xl bg-purple-700/60 pointer-events-none" data-id="element-1014" />
          <div className="absolute inset-0 rounded-xl bg-black/[0.05] group-hover:bg-transparent transition-colors duration-300" data-id="element-1015" />
          <div className="absolute inset-[1px] rounded-xl border border-white/20 pointer-events-none" data-id="element-1016" />
          <Sparkles size={14} className="relative z-10 text-white drop-shadow-sm" data-id="element-1017" />
          <span className="relative z-10 text-white drop-shadow-sm hidden sm:inline" data-id="element-1018">
            Athena AI
          </span>
        </button>
      </div>
    </header>;
}